// LocalStorage-backed progress store with tiny subscription.
import { evaluateBadges } from "./badges";
import { SECTIONS } from "./content";

const KEY = "ll_progress_v1";
const CATS = ["abc", "numbers", "colors", "shapes", "animals", "fruits", "everyday", "matching", "counting", "tracing", "memory", "games"];

const DEFAULT = {
  stars: 0,
  streak: 0,
  lastPlayedDate: null,
  activitiesCompleted: 0,
  dailyCompletions: 0,
  categories: Object.fromEntries(CATS.map(c => [c, { correct: 0, total: 0 }])),
  modesCompleted: {},
  weeklyActivity: {},
  dailyAdventure: { date: null, tasks: [], completed: false },
  badges: [],
  profile: { name: "", avatar: "boy", setup: false },
};

const todayStr = () => new Date().toISOString().slice(0, 10);
const clone = (o) => JSON.parse(JSON.stringify(o));
const subs = new Set();
let pendingBadges = [];

function read() {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return clone(DEFAULT);
    const parsed = JSON.parse(raw);
    const s = { ...clone(DEFAULT), ...parsed, categories: { ...clone(DEFAULT.categories), ...(parsed.categories || {}) }, profile: { ...DEFAULT.profile, ...(parsed.profile || {}) } };
    if (!Array.isArray(s.dailyAdventure?.tasks)) s.dailyAdventure = clone(DEFAULT.dailyAdventure);
    if (!s.modesCompleted || typeof s.modesCompleted !== "object") s.modesCompleted = {};
    if (!Array.isArray(s.badges)) s.badges = [];
    return s;
  } catch { return clone(DEFAULT); }
}

function write(state) {
  const { badges, fresh } = evaluateBadges(state);
  state.badges = badges;
  if (fresh.length) { state.stars += fresh.length * 3; pendingBadges.push(...fresh); }
  try { localStorage.setItem(KEY, JSON.stringify(state)); } catch {}
  subs.forEach(fn => fn(state));
}

export function subscribe(fn) { subs.add(fn); return () => subs.delete(fn); }
export function getState() { return read(); }
export function takeNewBadges() { const b = pendingBadges; pendingBadges = []; return b; }

function seededPick(seed, n, arr) {
  let x = seed; const out = [], pool = [...arr];
  while (out.length < n && pool.length) { x = (x * 9301 + 49297) % 233280; out.push(pool.splice(x % pool.length, 1)[0]); }
  return out;
}

export function ensureDailyAdventure() {
  const s = read();
  const d = todayStr();
  if (s.dailyAdventure?.date !== d) {
    const seed = Number(d.replace(/-/g, "")) % 100000;
    const count = 3 + (seed % 3);
    const all = SECTIONS.flatMap(sec => sec.modes.map(m => ({ id: `${sec.id}:${m.id}`, section: sec.id, mode: m.id, title: `${sec.short}: ${m.title}`, emoji: m.emoji, path: `${sec.path}?mode=${m.id}` })));
    s.dailyAdventure = { date: d, tasks: seededPick(seed, count, all).map(t => ({ ...t, done: false })), completed: false };
    write(s);
  }
  return s.dailyAdventure;
}

function bumpStreak(s) {
  const d = todayStr();
  if (s.lastPlayedDate !== d) {
    const yesterday = new Date(Date.now() - 86400000).toISOString().slice(0, 10);
    s.streak = s.lastPlayedDate === yesterday ? (s.streak || 0) + 1 : 1;
    s.lastPlayedDate = d;
  }
}

export function recordAnswer(category, isCorrect) {
  const s = read();
  const cat = s.categories[category] || { correct: 0, total: 0 };
  cat.total += 1;
  if (isCorrect) { cat.correct += 1; s.stars += 1; }
  s.categories[category] = cat;
  s.weeklyActivity[category] = (s.weeklyActivity[category] || 0) + (isCorrect ? 1 : 0);
  bumpStreak(s);
  write(s);
  return s;
}

export function completeActivity(category, modeId, bonus = 0) {
  const s = read();
  s.activitiesCompleted += 1;
  s.stars += bonus;
  if (modeId) {
    s.modesCompleted[category] = s.modesCompleted[category] || {};
    s.modesCompleted[category][modeId] = (s.modesCompleted[category][modeId] || 0) + 1;
  }
  const d = todayStr();
  if (s.dailyAdventure?.date === d && !s.dailyAdventure.completed) {
    const id = `${category}:${modeId}`;
    const task = s.dailyAdventure.tasks.find(t => !t.done && t.id === id);
    if (task) task.done = true;
    if (s.dailyAdventure.tasks.every(t => t.done)) { s.dailyAdventure.completed = true; s.stars += 10; s.dailyCompletions = (s.dailyCompletions || 0) + 1; }
  }
  bumpStreak(s);
  write(s);
  return s;
}

export function categoryPercent(cat) {
  const c = read().categories[cat];
  if (!c || c.total === 0) return 0;
  return Math.round((c.correct / c.total) * 100);
}

export function sectionMastery(sectionId) {
  const sec = SECTIONS.find(x => x.id === sectionId);
  if (!sec) return categoryPercent(sectionId);
  const done = read().modesCompleted[sectionId] || {};
  const tried = sec.modes.filter(m => done[m.id] > 0).length;
  return Math.round((tried / sec.modes.length) * 100);
}

export function overallProgress() {
  const s = read();
  const totalModes = SECTIONS.reduce((a, sec) => a + sec.modes.length, 0);
  const tried = SECTIONS.reduce((a, sec) => a + sec.modes.filter(m => (s.modesCompleted[sec.id] || {})[m.id] > 0).length, 0);
  return Math.round((tried / totalModes) * 100);
}

export function resetProgress() {
  const profile = read().profile;
  write({ ...clone(DEFAULT), profile });
}

export function setProfile(profile) {
  const s = read();
  s.profile = { ...s.profile, ...profile, setup: true };
  write(s);
}
